import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { rolesApi } from '../api/endpoints/roles';
import type { RoleRequest, PermissionRequest } from '../types/role';

export function useRoles() {
  const queryClient = useQueryClient();

  const useList = () =>
    useQuery({
      queryKey: ['admin-roles'],
      queryFn: () => rolesApi.getAll().then((r) => r.data.data ?? [])
    });

  const usePermissions = (roleId: string | null) =>
    useQuery({
      queryKey: ['admin-role-permissions', roleId],
      queryFn: () => rolesApi.getPermissions(roleId!).then((r) => r.data.data),
      enabled: !!roleId,

    });

  const useRegistry = () =>
    useQuery({
      queryKey: ['admin-permission-registry'],
      queryFn: () => rolesApi.getRegistry().then((r) => r.data.data),
      staleTime: 30 * 60 * 1000,
    });

  const useCreate = () =>
    useMutation({
      mutationFn: (data: RoleRequest) => rolesApi.create(data),
      onSuccess: () => {
        toast.success('Role created');
        queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      },
      onError: () => toast.error('Failed to create role'),
    });

  const useUpdate = () =>
    useMutation({
      mutationFn: ({ id, data }: { id: string; data: RoleRequest }) =>
        rolesApi.update(id, data),
      onSuccess: () => {
        toast.success('Role updated');
        queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      },
      onError: () => toast.error('Failed to update role'),
    });

  const useDelete = () =>
    useMutation({
      mutationFn: (id: string) => rolesApi.delete(id),
      onSuccess: () => {
        toast.success('Role deleted');
        queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      },
      onError: (e: any) =>
        toast.error(e?.response?.data?.messages?.[0] ?? 'Failed to delete role'),
    });

  const useUpdatePermissions = () =>
    useMutation({
      mutationFn: ({ id, data }: { id: string; data: PermissionRequest }) =>
        rolesApi.updatePermissions(id, data),
      onSuccess: (_, { id }) => {
        toast.success('Permissions saved');
        queryClient.invalidateQueries({ queryKey: ['admin-role-permissions', id] });
        queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      },
      onError: () => toast.error('Failed to save permissions'),
    });

  return { useList, usePermissions, useRegistry, useCreate, useUpdate, useDelete, useUpdatePermissions };
}
